import { useState, useEffect } from 'react';
import { Grid, ExternalLink, Loader2 } from 'lucide-react';
import { appService, App } from '@/services/appService';

interface AppSwitcherProps {
  className?: string;
}

export default function AppSwitcher({ className = '' }: AppSwitcherProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [apps, setApps] = useState<App[]>([]);
  const [loading, setLoading] = useState(false);
  const [openingApp, setOpeningApp] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && apps.length === 0) {
      loadApps();
    }
  }, [isOpen]);

  const loadApps = async () => {
    setLoading(true);
    try {
      const userApps = await appService.getUserApps();
      setApps(userApps);
    } catch (error) {
      console.error('Failed to load apps:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenApp = async (app: App) => {
    setOpeningApp(app.appCode);
    try {
      await appService.openAppWithSSO(app.appCode);
    } catch (error) {
      console.error('Failed to open app:', error);
      alert('Failed to open app. Please try again.');
    } finally {
      setOpeningApp(null);
      setIsOpen(false);
    }
  };

  return (
    <div className={`relative ${className}`}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
        title="Switch App"
      >
        <Grid className="w-5 h-5 text-gray-600" />
      </button>

      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />

          {/* Dropdown */}
          <div className="absolute right-0 mt-2 w-72 bg-white rounded-xl shadow-xl border border-gray-200 z-50 overflow-hidden">
            <div className="p-3 border-b border-gray-100">
              <h3 className="text-sm font-semibold text-gray-900">Your Apps</h3>
              <p className="text-xs text-gray-500">Switch to another app</p>
            </div>

            <div className="max-h-80 overflow-y-auto">
              {loading ? (
                <div className="p-4 text-center">
                  <Loader2 className="w-6 h-6 text-blue-600 animate-spin mx-auto" />
                  <p className="mt-2 text-sm text-gray-500">Loading apps...</p>
                </div>
              ) : apps.length === 0 ? (
                <div className="p-4 text-center">
                  <p className="text-sm text-gray-500">No apps available</p>
                  <p className="text-xs text-gray-400 mt-1">
                    Contact your admin for access
                  </p>
                </div>
              ) : (
                <div className="p-2">
                  {apps.map((app) => (
                    <button
                      key={app.appCode}
                      onClick={() => handleOpenApp(app)}
                      disabled={openingApp === app.appCode}
                      className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors text-left disabled:opacity-50"
                    >
                      {/* App Logo */}
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        {app.logoUrl ? (
                          <img
                            src={app.logoUrl}
                            alt={app.appName}
                            className="w-6 h-6 object-contain"
                          />
                        ) : (
                          <span className="text-blue-600 font-bold text-sm">
                            {app.appName.charAt(0).toUpperCase()}
                          </span>
                        )}
                      </div>

                      {/* App Info */}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {app.appName}
                        </p>
                        {app.description && (
                          <p className="text-xs text-gray-500 truncate">
                            {app.description}
                          </p>
                        )}
                      </div>

                      {/* Icon */}
                      {openingApp === app.appCode ? (
                        <Loader2 className="w-4 h-4 text-gray-400 animate-spin" />
                      ) : (
                        <ExternalLink className="w-4 h-4 text-gray-400" />
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="p-3 border-t border-gray-100 bg-gray-50">
              <p className="text-xs text-gray-500 text-center">
                Currently in: <span className="font-medium">ZTool Manager</span>
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
